"use client";

import { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { FileText, Trees, X } from "lucide-react"; // 引入 X 關閉圖標

//  核心修正：定義傳入參數規格 (接收 slug 與 onClose 關閉動作)
interface ProductDetailProps {
  slug: string | null;
  onClose: () => void;
}
export default function ProductDetail({
  slug,
  onClose
}: ProductDetailProps) {
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  //  核心修正：將主頁面的 body 防滾動鎖定邏輯內聚至此
  useEffect(() => {
    if (slug) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [slug]);

  // 透過 slug 串接 WordPress API 的生命週期
  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const response = await fetch(`/wp-json/wp/v2/flooring?slug=${slug}&_embed`);
        const data = await response.json();
        if (data && data.length > 0) {
          setProduct(data[0]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  //  如果狀態沒有傳入任何商品 slug，代表關閉狀態，React 直接不渲染任何東西
  if (!slug) return null;
  return (
    //  核心修正：包覆全螢幕 fixed 彈窗外殼與淡入動畫
    <div className="fixed inset-0 z-[100] bg-background flex flex-col animate-in fade-in duration-300">
      
      {/*  核心修正：原主頁面的固定頂部導覽列完美封裝進來 */}
      <div className="sticky top-0 z-[110] flex items-center justify-between px-6 py-4 bg-background/80 backdrop-blur-md border-b border-border/40">
        <div className="flex items-center gap-3">
          <div className="size-2 bg-primary rounded-full animate-pulse" />
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
            Product Details
          </span>
        </div>
        
        {/* 關閉按鈕 */}
        <Button variant="ghost" size="sm" onClick={onClose} className="group flex items-center gap-2 hover:bg-secondary/50 rounded-full transition-all">
          <span className="text-[10px] font-bold tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-all">
            Close
          </span>
          <X className="size-5 text-[#3d3d3d]" />
        </Button>
      </div>

      {/* 獨立滾動容器：完美承載你原本寫的所有 UI 排版與 API 內容 */}
      <div className="flex-1 overflow-y-auto bg-background" style={{
        WebkitOverflowScrolling: 'touch'
      }}>
        
        {loading ? <div className="flex flex-col items-center justify-center py-32 space-y-4">
            <div className="size-10 border-4 border-[#4A3728]/10 border-t-[#4A3728] rounded-full animate-spin"></div>
            <p className="text-lg text-[#4A3728] animate-pulse font-serif">載入中...</p>
          </div> : product ? (() => {
          const title = product.title?.rendered;
          const content = product.content?.rendered;
          const featuredMedia = product._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
          return <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                  
                  {/* 左側：圖片與特色標籤 */}
                  <div className="w-full lg:sticky lg:top-8">
                    <div className="rounded-sm overflow-hidden border border-[#4A3728]/10 shadow-xl bg-white">
                      <AspectRatio ratio={1}>
                        <img src={featuredMedia || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618354/img/flooring_product_fallback.png"} alt={title} className="w-full h-full object-cover" />
                      </AspectRatio>
                    </div>
                    
                    <div className="mt-6 p-4 bg-[#4A3728]/5 rounded-sm border border-[#4A3728]/10 hidden lg:flex items-center gap-4">
                      <Trees className="size-6 text-[#4A3728]/60 shrink-0" />
                      <p className="text-xs text-[#4A3728] font-sans">
                        天然木質感。每塊地板皆有獨一無二的紋理。<br />Unique wood patterns on every piece.
                      </p>
                    </div>
                  </div>

                  {/* 右側：可滑動內容與預約按鈕 */}
                  <div className="w-full flex flex-col space-y-10 pb-20">
                    <div className="text-center lg:text-left">
                      <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#4A3728] leading-tight mb-4" dangerouslySetInnerHTML={{
                    __html: title
                  }} />
                      <div className="h-1 bg-[#4A3728] w-20 mx-auto lg:mx-0 opacity-80" />
                    </div>

                    {/* WordPress 內文區 */}
                    <div className="prose prose-stone max-w-none text-muted-foreground leading-relaxed font-sans
                        prose-img:rounded-sm prose-img:shadow-md" dangerouslySetInnerHTML={{
                  __html: content
                }} />
                    
                    <div className="pt-4">
                      <Link to="/ContactUsPage" className="w-full">
                        <Button size="lg" className="w-full bg-[#4A3728] text-white hover:bg-[#4A3728]/90 py-8 text-lg font-serif shadow-lg">
                          預約看樣 Request Consultation
                        </Button>
                      </Link>
                    </div>
                  </div>

                </div>
              </main>;
        })() : null}

      </div>
    </div>
  );
}